import 'react-native-gesture-handler';
import '@/services/appleTripSurfaceBackgroundTask';

import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Animated,
  AppState,
  Easing,
  ImageBackground,
  InteractionManager,
  LogBox,
  Platform,
  View,
  type AppStateStatus,
} from 'react-native';
import { Stack, useGlobalSearchParams, usePathname, useRouter, useSegments } from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import Constants from 'expo-constants';
import { useFonts } from 'expo-font';
import { Ionicons } from '@expo/vector-icons';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, collection, query, where, getDocs, updateDoc, setDoc, arrayUnion, serverTimestamp, onSnapshot } from 'firebase/firestore';
import * as Linking from 'expo-linking';
import * as Notifications from 'expo-notifications';
import * as SplashScreen from 'expo-splash-screen';
import { ThemeProvider, useTheme } from '@/theme';
import { ToastProvider } from '@/components/ui/ToastProvider';
import { auth, db, installFirebaseRuntimeErrorHandlers } from '@/lib/firebase';
import { useUserStore, type UserProfile } from '@/state/userStore';
import {
  addBreadcrumb,
  captureException,
  clearUserContext,
  initErrorTracking,
  logStagingSentryDiagnosticsToConsole,
  setUserContext,
} from '@/services/errorTracking';
import { logger } from '@/services/logger';
import { isMapboxTokenConfigured } from '@/config/mapbox';
import { CookieConsentBanner } from '@/components/privacy/CookieConsentBanner';
import { PremiumGateModal } from '@/components/premium/PremiumGateModal';
import { PremiumUpsellModal } from '@/components/premium/PremiumUpsellModal';
import { PremiumUpsellAutoTrigger } from '@/components/premium/PremiumUpsellAutoTrigger';
import { usePremiumGateStore } from '@/state/premiumGateStore';
import { toIso2CountryCode } from '@/utils/countryIso2';
import type { PlanEntitlement, PlanTier, EntitlementStatus, EntitlementProvider } from '@/types/entitlements';
import { PortalHost, PortalProvider } from '@gorhom/portal';
import { acceptInviteLink } from '@/services/collaboration';
import { TripErrorBoundary } from '@/utils/errorBoundary';
import { initRevenueCat, loginRevenueCatUser, logoutRevenueCatUser } from '@/services/revenuecat';
import { completeOAuthWebSession } from '@/utils/completeOAuthWebSession';
import { runSignedOutSessionCleanup } from '@/services/authSessionCleanup';
import {
  loadAuthBootstrapSnapshot,
  loadLastSignedInSnapshot,
  saveAuthBootstrapSnapshot,
  type AuthBootstrapSnapshot,
} from '@/services/authBootstrapCache';
import { markStartupMetric } from '@/utils/startupMetrics';
import { saveLastKnownEntitlementUi } from '@/services/entitlementUiCache';
import { checkAndAcceptPendingInvitations } from '@/services/pendingCollaboration';
import {
  consumePendingNavigationIntent,
  getPendingNavigationIntent,
  setPendingNavigationIntent,
  toPendingIntentParams,
} from '@/navigation/pendingNavigationIntent';
import { getTripById, notifyLiveTripUpdated } from '@/services/trips';
import { logAnalyticsEvent } from '@/services/analyticsLogger';
import {
  isTemplatesDeepLinkPath,
  normalizeTemplateIdParam,
  normalizeTemplateTopicParam,
} from '@/navigation/templateDeepLink';
import { tryAutoCreateFirstTripFromTemplate } from '@/services/onboardingFirstTrip';
import { useTripSelectionStore } from '@/state/tripSelectionStore';
import { trackRetentionSession } from '@/services/retentionTracking';
import {
  logTemplateDeepLinkAnalytics,
  processAppleSurfaceQueryParams,
  queryStringParam,
} from '@/services/appleDeepLinkSurface';
import { purgeTripSurfaceSpotlightItems } from '@/services/appleWidgetV1';
import { useAppleTripSurfaceSync } from '@/hooks/useAppleTripSurfaceSync';
import {
  handleTripSurfaceNotificationReceived,
  registerAppleTripSurfaceBackgroundTask,
} from '@/services/appleTripSurfaceBackgroundTask';
import { applePlatformFlags } from '@/config/applePlatformFlags';

const STARTUP_TRACE_T0 = Date.now();

// Native splash stays up until hideAsync — without this, cold widget/Live Activity launches can
// show the splash indefinitely even after JS has mounted (expo-router does not always auto-hide).
void SplashScreen.preventAutoHideAsync().catch(() => {});

const startupSplashImage = require('../assets/splash-illustration-v2-text.png');
const startupLogoImage = require('../assets/splash-logo-minimal.png');
const startupAppIconImage = require('../assets/splash-icon.png');

function safeCompleteOAuthWebSession(source: string): void {
  try {
    const maybeComplete = completeOAuthWebSession as unknown;
    if (typeof maybeComplete === 'function') {
      maybeComplete();
    } else {
      logStartupStep('oauth_complete_skipped_non_function', { source });
    }
  } catch (error) {
    logStartupStep('oauth_complete_failed', {
      source,
      message: error instanceof Error ? error.message : String(error),
    });
  }
}

console.warn(`[startup] app/_layout module evaluation started t=${Date.now() - STARTUP_TRACE_T0}ms`);
markStartupMetric('app_layout_module_evaluated');
installFirebaseRuntimeErrorHandlers();

// Keep the app resilient when optional bottom-sheet native code is unavailable.
type ProviderProps = { children: React.ReactNode };

type BottomSheetModule = { BottomSheetModalProvider: React.ComponentType<ProviderProps> };

let isExpoGoCached: boolean | null = null;
let cachedBottomSheetModule: BottomSheetModule | null | undefined;

const initializedFcmTokenForUsers = new Set<string>();

function logStartupStep(step: string, data?: Record<string, unknown>) {
  const payload = {
    elapsedMs: Date.now() - STARTUP_TRACE_T0,
    platform: Platform.OS,
    ...data,
  };

  try {
    console.warn(`[startup] ${step}`);
  } catch {
    console.warn(`[startup] ${step}`);
  }

  try {
    addBreadcrumb(step, 'startup', 'info', payload);
  } catch {
    // ignore breadcrumb failures during boot tracing
  }
}

logStartupStep('app_layout_module_ready', {
  appOwnership: Constants?.appOwnership ?? null,
  executionEnvironment: Constants?.executionEnvironment ?? null,
});

const isExpoGo = (): boolean => {
  if (isExpoGoCached !== null) return isExpoGoCached;
  try {
    if (Constants?.appOwnership === 'expo' || Constants?.executionEnvironment === 'storeClient') {
      isExpoGoCached = true;
      return true;
    }
  } catch {
    // ignore
  }
  try {
    const globalAny = global as any;
    if (globalAny?.ExpoGo === true) {
      isExpoGoCached = true;
      return true;
    }
  } catch {
    // ignore
  }
  isExpoGoCached = false;
  return false;
};

const loadBottomSheetModule = (): BottomSheetModule | null => {
  if (isExpoGo()) {
    cachedBottomSheetModule = null;
    return null;
  }
  if (cachedBottomSheetModule !== undefined) return cachedBottomSheetModule;
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    cachedBottomSheetModule = require('@gorhom/bottom-sheet') as BottomSheetModule;
  } catch {
    cachedBottomSheetModule = null;
  }
  return cachedBottomSheetModule;
};

// Suppress deprecation warnings from third-party libraries
// These warnings come from React Navigation's internal code and don't affect functionality
LogBox.ignoreLogs([
  'props.pointerEvents is deprecated. Use style.pointerEvents',
]);

// Also suppress on web (LogBox doesn't work on web)
if (typeof window !== 'undefined') {
  const originalWarn = console.warn;
  console.warn = (...args: any[]) => {
    const message = args[0];
    if (
      typeof message === 'string' &&
      message.includes('props.pointerEvents is deprecated')
    ) {
      // Suppress this specific warning
      return;
    }
    originalWarn.apply(console, args);
  };
}

const shouldRequireEmailVerification = (
  firebaseUser:
    | {
        email?: string | null;
        emailVerified?: boolean;
        providerData?: Array<{ providerId?: string | null }>;
      }
    | null
    | undefined,
): boolean => {
  if (!firebaseUser?.email) return false;
  const hasPasswordProvider = (firebaseUser.providerData || []).some(
    (provider) => provider?.providerId === 'password',
  );
  return hasPasswordProvider && firebaseUser.emailVerified !== true;
};

function AuthInitializer() {
  const setAuthReady = useUserStore((state) => state.setAuthReady);
  const setFirebaseUser = useUserStore((state) => state.setFirebaseUser);
  const setNeedsEmailVerification = useUserStore((state) => state.setNeedsEmailVerification);
  const hydrateProfile = useUserStore((state) => state.hydrateProfile);
  const setProfileDetails = useUserStore((state) => state.setProfileDetails);
  const reset = useUserStore((state) => state.reset);
  const setShouldShowTutorial = useUserStore((state) => state.setShouldShowTutorial);
  const completeTutorial = useUserStore((state) => state.completeTutorial);

  useEffect(() => {
    let isMounted = true;
    let unsubscribe: (() => void) | null = null;
    let profileUnsubscribe: (() => void) | null = null;
    let callbackFired = false;

    logStartupStep('auth_initializer_effect_start');

    const applyAuthBootstrapSnapshot = (cachedSnapshot: AuthBootstrapSnapshot) => {
      setFirebaseUser({ uid: cachedSnapshot.uid, email: undefined });
      hydrateProfile(
        cachedSnapshot.profile,
        cachedSnapshot.hasCompletedOnboarding,
        cachedSnapshot.isNewUser,
      );
      if (cachedSnapshot.hasCompletedTutorial) {
        completeTutorial();
      } else {
        setShouldShowTutorial(cachedSnapshot.shouldShowTutorial);
      }
    };

    const hydrateAuthFromDiskCache = async (): Promise<boolean> => {
      try {
        const cachedSnapshot = await loadLastSignedInSnapshot();
        if (!cachedSnapshot || !isMounted) {
          return false;
        }
        logStartupStep('auth_disk_cache_hit', {
          uid: cachedSnapshot.uid,
          hasCompletedOnboarding: cachedSnapshot.hasCompletedOnboarding,
        });
        applyAuthBootstrapSnapshot(cachedSnapshot);
        setAuthReady(true);
        return true;
      } catch (e) {
        logStartupStep('auth_disk_cache_error', {
          message: e instanceof Error ? e.message : String(e),
        });
        return false;
      }
    };

    // Hydrate returning users immediately so widget/Live Activity deep links can navigate
    // without waiting for Firebase auth restore + RevenueCat + Firestore on cold launch.
    void hydrateAuthFromDiskCache();

    // Fallback timeout to ensure authReady is set even if onAuthStateChanged is slow.
    // On web, Firebase auth restore from IndexedDB can take 30+ seconds on some browsers/networks.
    // Instead of just giving up and sending the user to sign-in, try to hydrate from our own
    // bootstrap cache first so returning users go straight to tabs.
    const AUTH_INIT_FALLBACK_MS = 3000;
    const fallbackTimeout = setTimeout(() => {
      if (!isMounted || callbackFired) return;

      logStartupStep('auth_initializer_fallback_timeout_fired', {
        callbackFired,
        timeoutMs: AUTH_INIT_FALLBACK_MS,
      });

      void (async () => {
        if (!isMounted || callbackFired || useUserStore.getState().authReady) {
          return;
        }
        const hydrated = await hydrateAuthFromDiskCache();
        if (!hydrated) {
          logStartupStep('auth_fallback_no_cache', {});
        }
        if (isMounted && !callbackFired && !useUserStore.getState().authReady) {
          console.warn(
            `[AuthInitializer] Fallback: setting authReady after ${AUTH_INIT_FALLBACK_MS}ms (onAuthStateChanged not yet fired)`,
          );
          setAuthReady(true);
        }
      })();
    }, AUTH_INIT_FALLBACK_MS);

    // If `onAuthStateChanged` already ran (callbackFired) but `processAuthState` never finishes
    // (hung await — e.g. billing SDK, Firestore), RootIndex stays on "Initializing..." forever.
    // Unblock after a bounded wait; restore onboarding flags from disk cache when possible.
    const PROFILE_BOOTSTRAP_WATCHDOG_MS = 8_000;
    const profileBootstrapWatchdog = setTimeout(() => {
      if (!isMounted || useUserStore.getState().authReady) {
        return;
      }
      void (async () => {
        const uid = useUserStore.getState().firebaseUser?.uid;
        logStartupStep('auth_initializer_profile_watchdog_fired', {
          uid: uid ?? null,
        });
        logger.warn(
          '[AuthInitializer] Profile bootstrap watchdog: auth still not ready; forcing authReady. Investigate hung awaits in processAuthState if this repeats.',
          { uid: uid ?? null },
        );
        try {
          if (uid) {
            const cachedSnapshot = await loadAuthBootstrapSnapshot(uid);
            if (cachedSnapshot && isMounted) {
              hydrateProfile(
                cachedSnapshot.profile,
                cachedSnapshot.hasCompletedOnboarding,
                cachedSnapshot.isNewUser,
              );
              if (cachedSnapshot.hasCompletedTutorial) {
                completeTutorial();
              } else {
                setShouldShowTutorial(cachedSnapshot.shouldShowTutorial);
              }
            }
          }
        } catch (e) {
          logger.warn(
            'Auth bootstrap watchdog: failed to hydrate from cache',
            undefined,
            e instanceof Error ? e : new Error(String(e)),
          );
        } finally {
          if (isMounted) {
            setAuthReady(true);
          }
        }
      })();
    }, PROFILE_BOOTSTRAP_WATCHDOG_MS);

    // Helper function to process auth state
    const processAuthState = async (firebaseUser: any) => {
      if (!isMounted) return;

      logStartupStep('process_auth_state_start', {
        hasUser: Boolean(firebaseUser),
        uid: firebaseUser?.uid ?? null,
        hasEmail: Boolean(firebaseUser?.email),
      });
      
      try {
        if (firebaseUser) {
          setFirebaseUser({ uid: firebaseUser.uid, email: firebaseUser.email });
          setNeedsEmailVerification(shouldRequireEmailVerification(firebaseUser));
          logStartupStep('revenuecat_login_start', { uid: firebaseUser.uid });
          await loginRevenueCatUser(firebaseUser.uid).catch((error) => {
            logger.warn(
              'Failed to log in RevenueCat user',
              undefined,
              error instanceof Error ? error : new Error(String(error)),
            );
          });
          logStartupStep('revenuecat_login_done', { uid: firebaseUser.uid });
          if (profileUnsubscribe) {
            profileUnsubscribe();
            profileUnsubscribe = null;
          }
          
          // Set user context for error tracking
          setUserContext(firebaseUser.uid, firebaseUser.email || undefined);

          try {
            const profileRef = doc(db, 'users', firebaseUser.uid);
            const PROFILE_GET_TIMEOUT_MS = 25_000;
            logStartupStep('profile_get_start', { uid: firebaseUser.uid });
            const snapshot = await Promise.race([
              getDoc(profileRef),
              new Promise<never>((_, reject) =>
                setTimeout(
                  () => reject(new Error('firestore_users_getdoc_timeout')),
                  PROFILE_GET_TIMEOUT_MS,
                ),
              ),
            ]);

            if (snapshot.exists()) {
              const data = snapshot.data();
              logStartupStep('profile_get_success', {
                uid: firebaseUser.uid,
                exists: true,
                onboardingCompleted: Boolean(data.onboardingCompleted),
              });
              const hasCompleted = Boolean(data.onboardingCompleted);
              const hasCompletedTutorial = Boolean(data.tutorialCompleted);
              const isTutorialEligible = Boolean(data.postOnboardingTutorialEligible);
              
              // Backfill/migrate user profile fields (non-breaking):
              // - Ensure email is set in the user document (for collaboration lookups)
              // - Ensure display name from Auth (SIWA/SIWG) is mirrored when Firestore lacks it
              // - Ensure ISO2 country codes are present for visa map/check integrations
              const updates: Record<string, any> = {};

              let profileNameForUi: string | undefined =
                typeof data.name === 'string' && data.name.trim().length > 0
                  ? data.name.trim()
                  : undefined;
              const authDisplayName =
                typeof firebaseUser.displayName === 'string' ? firebaseUser.displayName.trim() : '';

              if (firebaseUser.email && (!data.email || data.email !== firebaseUser.email.toLowerCase().trim())) {
                updates.email = firebaseUser.email.toLowerCase().trim();
              }

              if (authDisplayName && !profileNameForUi) {
                updates.name = authDisplayName;
                profileNameForUi = authDisplayName;
              }

              const citizenshipIso2 =
                data.citizenshipIso2 ||
                (typeof data.citizenship === 'string'
                  ? toIso2CountryCode(data.citizenship)
                  : undefined);
              const homeCountryIso2 =
                data.homeCountryIso2 ||
                (typeof data.homeCountry === 'string'
                  ? toIso2CountryCode(data.homeCountry)
                  : undefined);

              if (!data.citizenshipIso2 && citizenshipIso2) {
                updates.citizenshipIso2 = citizenshipIso2;
              }
              if (!data.homeCountryIso2 && homeCountryIso2) {
                updates.homeCountryIso2 = homeCountryIso2;
              }

              if (Object.keys(updates).length > 0) {
                await updateDoc(profileRef, {
                  ...updates,
                  updatedAt: serverTimestamp(),
                });
              }
              
              const hydratedProfile = {
                name: profileNameForUi ?? undefined,
                homeCountry: data.homeCountry ?? undefined,
                homeCountryIso2: homeCountryIso2 ?? undefined,
                homeCity: data.homeCity ?? undefined,
                citizenship: data.citizenship ?? undefined,
                citizenshipIso2: citizenshipIso2 ?? undefined,
                language: data.language ?? undefined,
                avatar: data.avatar ?? undefined,
                travelType: data.travelType ?? undefined,
                // Legacy + normalized entitlement shape. `isPremium` remains for compatibility.
                isPremium: Boolean(data.isPremium) || data.planTier === 'premium',
                planTier: data.planTier === 'premium' ? 'premium' : 'free',
                entitlement: {
                  tier: data.planTier === 'premium' || Boolean(data.isPremium) ? 'premium' : 'free',
                  status:
                    data.subscriptionStatus === 'active' ||
                    data.subscriptionStatus === 'grace_period' ||
                    data.subscriptionStatus === 'cancelled' ||
                    data.subscriptionStatus === 'expired'
                      ? data.subscriptionStatus
                      : 'inactive',
                  provider:
                    data.entitlementProvider === 'stripe' ||
                    data.entitlementProvider === 'revenuecat' ||
                    data.entitlementProvider === 'unknown'
                      ? data.entitlementProvider
                      : 'manual',
                  expiresAt: data.entitlementExpiresAt?.toDate
                    ? data.entitlementExpiresAt.toDate().toISOString()
                    : undefined,
                  updatedAt: data.entitlementUpdatedAt?.toDate
                    ? data.entitlementUpdatedAt.toDate().toISOString()
                    : undefined,
                },
                notificationPreference:
                  data.notificationPreference && typeof data.notificationPreference === 'object'
                    ? data.notificationPreference
                    : undefined,
                preferences: (() => {
                  const raw = data.preferences;
                  if (!raw || typeof raw !== 'object') return undefined;
                  const safety = (raw as { safety?: unknown }).safety;
                  if (!safety || typeof safety !== 'object') return undefined;
                  const strict = (safety as { strictDestinationNews?: unknown }).strictDestinationNews;
                  if (typeof strict !== 'boolean') return undefined;
                  return { safety: { strictDestinationNews: strict } };
                })(),
              };

              hydrateProfile(hydratedProfile as UserProfile, hasCompleted, !hasCompleted);

              // Set tutorial status:
              // Show only for *new users* who are explicitly marked eligible after onboarding.
              if (hasCompletedTutorial) {
                completeTutorial();
              } else if (hasCompleted && isTutorialEligible) {
                setShouldShowTutorial(true);
              } else {
                // Returning user (or not eligible) → never show.
                setShouldShowTutorial(false);
              }

              saveAuthBootstrapSnapshot({
                uid: firebaseUser.uid,
                profile: hydratedProfile as UserProfile,
                hasCompletedOnboarding: hasCompleted,
                isNewUser: !hasCompleted,
                hasCompletedTutorial,
                shouldShowTutorial: hasCompleted ? isTutorialEligible && !hasCompletedTutorial : false,
              }).catch(() => {});

              void saveLastKnownEntitlementUi(firebaseUser.uid, {
                isPremium: hydratedProfile.isPremium,
                planTier: hydratedProfile.planTier as PlanTier | undefined,
                entitlement: hydratedProfile.entitlement as PlanEntitlement | undefined,
              });
            } else {
              logStartupStep('profile_get_success', {
                uid: firebaseUser.uid,
                exists: false,
              });
              // Create user document with email if it doesn't exist
              if (firebaseUser.email) {
                const initialName = typeof firebaseUser.displayName === 'string' ? firebaseUser.displayName.trim() : '';
                await setDoc(profileRef, {
                  email: firebaseUser.email.toLowerCase().trim(),
                  name: initialName.length > 0 ? initialName : null,
                  homeCity: null,
                  createdAt: serverTimestamp(),
                  updatedAt: serverTimestamp(),
                });
              }
              const bootstrapProfile: UserProfile =
                typeof firebaseUser.displayName === 'string' && firebaseUser.displayName.trim().length > 0
                  ? { name: firebaseUser.displayName.trim() }
                  : {};
              hydrateProfile(bootstrapProfile, false, true);
              setShouldShowTutorial(false);
              saveAuthBootstrapSnapshot({
                uid: firebaseUser.uid,
                profile: bootstrapProfile,
                hasCompletedOnboarding: false,
                isNewUser: true,
                hasCompletedTutorial: false,
                shouldShowTutorial: false,
              }).catch(() => {});
            }
            
            // Run these in background so sign-in navigation isn't delayed
            if (firebaseUser.email) {
              checkAndAcceptPendingInvitations(firebaseUser.uid, firebaseUser.email.toLowerCase().trim());
            }
            if (!initializedFcmTokenForUsers.has(firebaseUser.uid)) {
              initializedFcmTokenForUsers.add(firebaseUser.uid);
              import('@/services/fcmTokens')
                .then(({ initializeFCMToken }) => initializeFCMToken(firebaseUser.uid))
                .catch((error) => {
                  initializedFcmTokenForUsers.delete(firebaseUser.uid);
                  logger.warn('Failed to initialize FCM token', undefined, error as Error);
                });
            }
          } catch (error) {
            logger.warn(
              'Failed to load user profile',
              undefined,
              error instanceof Error ? error : new Error(String(error)),
            );
            const cachedSnapshot = await loadAuthBootstrapSnapshot(firebaseUser.uid);
            if (cachedSnapshot) {
              hydrateProfile(
                cachedSnapshot.profile,
                cachedSnapshot.hasCompletedOnboarding,
                cachedSnapshot.isNewUser,
              );
              if (cachedSnapshot.hasCompletedTutorial) {
                completeTutorial();
              } else {
                setShouldShowTutorial(cachedSnapshot.shouldShowTutorial);
              }
            } else {
              const currentState = useUserStore.getState();
              if (currentState.hasCompletedOnboarding || Object.keys(currentState.profile).length > 0) {
                hydrateProfile(
                  currentState.profile,
                  currentState.hasCompletedOnboarding,
                  currentState.isNewUser,
                );
              } else {
                hydrateProfile({}, false, true);
                setShouldShowTutorial(false);
              }
            }
          }
          // Guard against mounting an onSnapshot after the effect has torn down.
          // processAuthState is async (multiple awaits above), so the effect could
          // be cleaning up while this code runs — in that case the cleanup would
          // miss this new listener and we'd leak it.
          if (!isMounted) {
            logStartupStep('profile_snapshot_skipped_unmounted', { uid: firebaseUser.uid });
            return;
          }
          const profileRef = doc(db, 'users', firebaseUser.uid);
          const liveProfileUnsub = onSnapshot(
            profileRef,
            (profileSnap) => {
              if (!isMounted || !profileSnap.exists()) return;
              const live = profileSnap.data();
              const status: EntitlementStatus =
                live.subscriptionStatus === 'active' ||
                live.subscriptionStatus === 'grace_period' ||
                live.subscriptionStatus === 'cancelled' ||
                live.subscriptionStatus === 'expired'
                  ? live.subscriptionStatus
                  : 'inactive';
              const planTier: PlanTier = live.planTier === 'premium' ? 'premium' : 'free';
              const isPremium =
                Boolean(live.isPremium) || planTier === 'premium' || status === 'active' || status === 'grace_period';

              const livePrefs = live.preferences;
              const strictNews =
                livePrefs &&
                typeof livePrefs === 'object' &&
                livePrefs.safety &&
                typeof livePrefs.safety === 'object' &&
                typeof (livePrefs.safety as { strictDestinationNews?: unknown }).strictDestinationNews === 'boolean'
                  ? (livePrefs.safety as { strictDestinationNews: boolean }).strictDestinationNews
                  : undefined;

              const provider: EntitlementProvider =
                live.entitlementProvider === 'stripe' ||
                live.entitlementProvider === 'revenuecat' ||
                live.entitlementProvider === 'unknown'
                  ? live.entitlementProvider
                  : 'manual';

              const entitlement: PlanEntitlement = {
                tier: planTier,
                status,
                provider,
                expiresAt: live.entitlementExpiresAt?.toDate
                  ? live.entitlementExpiresAt.toDate().toISOString()
                  : undefined,
                updatedAt: live.entitlementUpdatedAt?.toDate
                  ? live.entitlementUpdatedAt.toDate().toISOString()
                  : undefined,
              };

              setProfileDetails({
                isPremium,
                planTier,
                entitlement,
                ...(typeof strictNews === 'boolean'
                  ? {
                      preferences: {
                        ...useUserStore.getState().profile.preferences,
                        safety: {
                          ...useUserStore.getState().profile.preferences?.safety,
                          strictDestinationNews: strictNews,
                        },
                      },
                    }
                  : {}),
              });

              void saveLastKnownEntitlementUi(firebaseUser.uid, {
                isPremium,
                planTier,
                entitlement,
              });
            },
            (error) => {
              logger.warn(
                'Failed to subscribe to live entitlement updates',
                undefined,
                error instanceof Error ? error : new Error(String(error)),
              );
            },
          );
          // Re-check after subscribe: if unmount raced between the `isMounted`
          // check above and onSnapshot returning, tear down immediately.
          if (!isMounted) {
            try {
              liveProfileUnsub();
            } catch {
              /* already unsubscribed */
            }
            logStartupStep('profile_snapshot_unsubscribed_post_race', { uid: firebaseUser.uid });
          } else {
            profileUnsubscribe = liveProfileUnsub;
            logStartupStep('profile_snapshot_subscribed', { uid: firebaseUser.uid });
          }
        } else {
          logStartupStep('process_auth_state_signed_out');
          initializedFcmTokenForUsers.clear();
          if (profileUnsubscribe) {
            profileUnsubscribe();
            profileUnsubscribe = null;
          }
          await runSignedOutSessionCleanup();
          await purgeTripSurfaceSpotlightItems().catch((error) => {
            logger.warn(
              'Failed to purge Apple Spotlight trip surface items on sign-out',
              undefined,
              error instanceof Error ? error : new Error(String(error)),
            );
          });
          setPendingNavigationIntent(null);
          logStartupStep('revenuecat_logout_start');
          await logoutRevenueCatUser().catch((error) => {
            logger.warn(
              'Failed to log out RevenueCat user',
              undefined,
              error instanceof Error ? error : new Error(String(error)),
            );
          });
          logStartupStep('revenuecat_logout_done');
          clearUserContext();
          reset();
        }
      } catch (error) {
        logStartupStep('process_auth_state_error', {
          message: error instanceof Error ? error.message : String(error),
        });
        console.error('[AuthInitializer] Error processing auth state:', error);
        logger.error('Error processing auth state', error as Error);
      } finally {
        logStartupStep('process_auth_state_finally', {
          hasUser: Boolean(firebaseUser),
        });
        // Always set authReady to true after processing
        if (isMounted) {
          setAuthReady(true);
        }
      }
    };

    try {
      // Do not call `processAuthState` here: `onAuthStateChanged` invokes the callback immediately
      // with the current user. A duplicate eager call was racing a second run (double RevenueCat login,
      // double Firestore reads) and could extend time-to-ready.

      // Set up listener for future auth state changes
      // onAuthStateChanged should fire immediately with current auth state
      unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        logStartupStep('auth_state_changed_callback', {
          hasUser: Boolean(firebaseUser),
          uid: firebaseUser?.uid ?? null,
        });
        if (__DEV__) {
          console.log('[AuthInitializer] onAuthStateChanged callback fired, user:', firebaseUser ? 'exists' : 'null');
        }

        if (!isMounted) return;

        // Mark that callback fired
        if (!callbackFired) {
          callbackFired = true;
          clearTimeout(fallbackTimeout);
          if (__DEV__) {
            console.log('[AuthInitializer] Callback fired, cleared fallback timeout');
          }
        }

        if (firebaseUser) {
          const prior = useUserStore.getState();
          const sameBootstrappedUser =
            prior.authReady &&
            prior.firebaseUser?.uid === firebaseUser.uid &&
            prior.hasCompletedOnboarding;
          // Only block navigation when auth identity/onboarding state may have changed.
          // Re-setting authReady=false after disk bootstrap caused cold widget launches to
          // sit on splash/loading while RevenueCat + Firestore finished.
          if (!sameBootstrappedUser) {
            setAuthReady(false);
          }
        }

        await processAuthState(firebaseUser);
      });

    } catch (error) {
      logStartupStep('auth_state_listener_setup_error', {
        message: error instanceof Error ? error.message : String(error),
      });
      console.error('[AuthInitializer] Error setting up auth state listener:', error);
      logger.error('Error setting up auth state listener', error as Error);
      // Clear fallback timeout and set authReady immediately on error
      clearTimeout(fallbackTimeout);
      if (isMounted) {
        setAuthReady(true);
        reset(); // Ensure state is reset
      }
    }

    return () => {
      logStartupStep('auth_initializer_cleanup');
      isMounted = false;
      clearTimeout(fallbackTimeout);
      clearTimeout(profileBootstrapWatchdog);
      if (unsubscribe) {
        unsubscribe();
      }
      if (profileUnsubscribe) {
        profileUnsubscribe();
      }
    };
  }, [
    completeTutorial,
    hydrateProfile,
    reset,
    setAuthReady,
    setFirebaseUser,
    setNeedsEmailVerification,
    setProfileDetails,
    setShouldShowTutorial,
  ]);

  return null;
}

function NotificationHandler() {
  const router = useRouter();

  useEffect(() => {
    if (Platform.OS === 'web') {
      return;
    }
    // Normalize notification payload values to router param types (string | number | undefined)
    const toParam = (v: unknown): string | number | undefined => {
      if (v === null || v === undefined) return undefined;
      if (typeof v === 'string' || typeof v === 'number') return v;
      return undefined;
    };

    const canRouteToTrip = async (tripParam: string | number | undefined): Promise<boolean> => {
      if (tripParam === undefined) {
        return false;
      }
      const candidate = String(tripParam).trim();
      if (!candidate) {
        return false;
      }
      try {
        const tripSnap = await getDoc(doc(db, 'trips', candidate));
        return tripSnap.exists();
      } catch (error) {
        // Fail-open on permission/network errors so valid notifications still route.
        const code = (error as { code?: string } | null)?.code;
        if (code === 'permission-denied' || code === 'missing-or-insufficient-permissions') {
          return true;
        }
        console.warn('[NotificationHandler] Failed trip existence check', { tripId: candidate, error });
        return false;
      }
    };

    const recoverSharedTripFromNotification = async (
      tripParam: string | number | undefined,
      type: unknown,
    ): Promise<void> => {
      if (type !== 'collaboration' || tripParam === undefined) {
        return;
      }
      const uid = auth.currentUser?.uid;
      const tripId = String(tripParam).trim();
      if (!uid || !tripId) {
        return;
      }
      const trip = await getTripById(tripId, { fromServer: true });
      if (trip) {
        notifyLiveTripUpdated(uid, trip);
      }
    };

    const handleNotificationResponse = (response: Notifications.NotificationResponse) => {
      void (async () => {
        const data = response.notification.request.content.data as Record<string, unknown> | undefined;
        const tripId = data?.tripId != null ? toParam(data.tripId) : undefined;
        const bookingId = data?.bookingId != null ? toParam(data.bookingId) : undefined;
        await recoverSharedTripFromNotification(tripId, data?.type);

        if (tripId !== undefined) {
          const canRoute = await canRouteToTrip(tripId);
          if (!canRoute) {
            console.info('[NotificationHandler] Ignoring notification for missing trip', {
              type: data?.type,
              tripId,
            });
            return;
          }
        }

        if (data?.type === 'day_briefing' && tripId !== undefined) {
          void logAnalyticsEvent('day_briefing_notification_open', {
            tripId: String(tripId),
            notificationId: response.notification.request.identifier,
          });
          router.push({
            pathname: '/(tabs)/plan',
            params: { tripId },
          });
        } else if (data?.type === 'collaboration' && tripId !== undefined) {
          router.push({
            pathname: '/(tabs)/plan',
            params: { tripId },
          });
        } else if (data?.type === 'maybe_idea_added' && tripId !== undefined) {
          router.push({
            pathname: '/(tabs)/plan',
            params: { tripId },
          });
        } else if (data?.type === 'comment_added' && tripId !== undefined) {
          router.push({
            pathname: '/(tabs)/plan',
            params: { tripId },
          });
        } else if (data?.type === 'navigation' && tripId !== undefined) {
          router.push({
            pathname: '/(tabs)/travel',
            params: { tripId, tab: 'maps' },
          });
        } else if (data?.type === 'budget_alert' && tripId !== undefined) {
          router.push({
            pathname: '/(tabs)/wallet',
            params: { tripId },
          });
        } else if ((data?.type === 'weather_alert' || data?.type === 'weather_forecast') && tripId !== undefined) {
          router.push({
            pathname: '/(tabs)/plan',
            params: { tripId },
          });
        } else if (
          (data?.type === 'flight_delay' ||
            data?.type === 'flight_gate_change' ||
            data?.type === 'flight_checkin') &&
          tripId !== undefined
        ) {
          router.push({
            pathname: '/(tabs)/plan',
            params: { tripId, ...(bookingId !== undefined && { bookingId }) },
          });
        } else if (
          (data?.type === 'expense_added' ||
            data?.type === 'expense_edited' ||
            data?.type === 'split_updated' ||
            data?.type === 'settlement_nudge') &&
          tripId !== undefined
        ) {
          router.push({
            pathname: '/(tabs)/wallet',
            params: { tripId },
          });
        }
      })();
    };

    const responseSubscription = Notifications.addNotificationResponseReceivedListener(handleNotificationResponse);

    const receivedSubscription = Notifications.addNotificationReceivedListener((notification) => {
      void (async () => {
        const data = notification.request.content.data as Record<string, unknown> | undefined;
        const tripId = data?.tripId != null ? toParam(data.tripId) : undefined;
        await recoverSharedTripFromNotification(tripId, data?.type);
        handleTripSurfaceNotificationReceived(data);
      })();
    });

    void Notifications.getLastNotificationResponseAsync()
      .then((response) => {
        if (response) {
          handleNotificationResponse(response);
        }
      })
      .catch((error) => {
        console.warn('[NotificationHandler] Failed to process last notification response', error);
      });

    return () => {
      responseSubscription.remove();
      receivedSubscription.remove();
    };
  }, [router]);

  return null;
}

function NavigationHandler() {
  const router = useRouter();
  const segments = useSegments();
  const pathname = usePathname();
  const routeParams = useGlobalSearchParams();
  const authReady = useUserStore((state) => state.authReady);
  const isAuthenticated = useUserStore((state) => state.isAuthenticated);
  const needsEmailVerification = useUserStore((state) => state.needsEmailVerification);
  const hasCompletedOnboarding = useUserStore((state) => state.hasCompletedOnboarding);
  const firebaseUser = useUserStore((state) => state.firebaseUser);
  const profileName = useUserStore((state) => state.profile?.name);
  const openPremiumGate = usePremiumGateStore((state) => state.open);

  useEffect(() => {
    if (!authReady) {
      return;
    }
    markStartupMetric('auth_ready');

    const inAuthGroup = segments[0] === '(onboarding)';
    const inTabsGroup = segments[0] === '(tabs)';
    /** Public post-action pages (e.g. Firebase `continueUrl`) live under `app/auth/*` — not `(onboarding)`. */
    const inPublicAuthGroup = segments[0] === 'auth';
    const isPublicJoinCollaborationRoute = segments[0] === 'join-collaboration';
    const isPublicInviteRoute = segments[0] === 'invite';
    const isPublicTemplatesRoute = segments[0] === 'templates';

    if (__DEV__) {
      console.log(
        'NavigationHandler - isAuthenticated:',
        isAuthenticated,
        'hasCompletedOnboarding:',
        hasCompletedOnboarding,
        'segments:',
        segments,
        'inAuthGroup:',
        inAuthGroup,
        'inTabsGroup:',
        inTabsGroup,
        'inPublicAuthGroup:',
        inPublicAuthGroup,
        'isPublicJoinCollaboration:',
        isPublicJoinCollaborationRoute,
        'isPublicInviteRoute:',
        isPublicInviteRoute,
      );
    }

    // If user is not authenticated, allow onboarding routes, otherwise redirect to sign-in options.
    if (!isAuthenticated) {
      if (
        !inAuthGroup &&
        !inPublicAuthGroup &&
        !isPublicJoinCollaborationRoute &&
        !isPublicInviteRoute &&
        !isPublicTemplatesRoute
      ) {
        if (pathname && pathname !== '/' && !pathname.startsWith('/(onboarding)')) {
          setPendingNavigationIntent({
            kind: 'route',
            pathname,
            params: toPendingIntentParams(routeParams),
          });
        }
        // User is not authenticated and not in auth group, redirect to sign-in options.
        // (value-proposition screen is handled by app/index.tsx)
        if (__DEV__) {
          console.log('NavigationHandler - User not authenticated, redirecting to sign-in');
        }
        try {
          router.replace('/(onboarding)/sign-in');
        } catch (error) {
          console.error('NavigationHandler - Error navigating to sign-in:', error);
          // Fallback
          setTimeout(() => {
            router.push('/(onboarding)/sign-in');
          }, 100);
        }
      }
      // Allow value-proposition screen (it's in the onboarding group)
    } else if (isAuthenticated && needsEmailVerification) {
      const authRoute = (segments as readonly string[])[1];
      const onJoinCollaborationLanding = segments[0] === 'join-collaboration';
      const onInviteLinkLanding = segments[0] === 'invite';
      const onPostVerifyLanding =
        inPublicAuthGroup && authRoute === 'email-verified';
      const allowUnverifiedRoute =
        onPostVerifyLanding ||
        onJoinCollaborationLanding ||
        onInviteLinkLanding ||
        (inAuthGroup &&
          (authRoute === 'verify-email' ||
            authRoute === 'privacy-policy' ||
            authRoute === 'terms-of-service'));

      if (!allowUnverifiedRoute) {
        router.replace('/(onboarding)/verify-email');
      }
      return;
    } else if (isAuthenticated && inAuthGroup) {
      const authRoute = (segments as readonly string[])[1];
      const rawMode = Array.isArray(routeParams.mode) ? routeParams.mode[0] : routeParams.mode;
      const isProfileEditRoute =
        rawMode === 'edit' &&
        (authRoute === 'profile-details' || authRoute === 'avatar' || authRoute === 'travel-type');
      const allowAuthRouteWhileAuthed =
        authRoute === 'privacy-policy' || authRoute === 'terms-of-service' || isProfileEditRoute;

      if (hasCompletedOnboarding && !allowAuthRouteWhileAuthed) {
        if (__DEV__) {
          console.log('NavigationHandler - User completed onboarding in auth group, redirecting to tabs');
        }
        router.replace('/(tabs)');
      } else if (!hasCompletedOnboarding && authRoute === 'sign-in') {
        // User needs onboarding, let index.tsx route them to the first unfinished step.
        if (__DEV__) {
          console.log('NavigationHandler - User authenticated but needs onboarding, redirecting to root');
        }
        router.replace('/');
      }
    }
  }, [authReady, isAuthenticated, needsEmailVerification, hasCompletedOnboarding, pathname, routeParams, segments, router]);

  useEffect(() => {
    if (!authReady || !isAuthenticated || !hasCompletedOnboarding || needsEmailVerification) {
      return;
    }
    const pendingIntent = getPendingNavigationIntent();
    if (!pendingIntent) return;

    void (async () => {
      const intent = consumePendingNavigationIntent();
      if (!intent) return;

      if (intent.kind === 'invite') {
        if (!firebaseUser?.uid || !firebaseUser?.email) {
          setPendingNavigationIntent(intent);
          return;
        }
        const inviteDisplayName = profileName?.trim() || undefined;
        const result = await acceptInviteLink(
          intent.linkId,
          firebaseUser.uid,
          firebaseUser.email,
          inviteDisplayName,
        );
        if (!result.success) {
          const isTripLimit = (result.error || '').toLowerCase().includes('free plan allows 1 active trip');
          if (isTripLimit) {
            openPremiumGate({
              feature: 'trips',
              title: 'Upgrade to Join Another Active Trip',
              message: 'Free users can only be on one active trip at a time. Upgrade to Premium for unlimited active trips.',
              cta: 'upgrade_now',
            });
            return;
          }
          Alert.alert('Invite Link Error', result.error || 'Unable to accept invite link.');
          return;
        }
        if (result.tripId) {
          router.replace({
            pathname: '/(tabs)/plan',
            params: { tripId: result.tripId },
          });
        } else {
          router.replace('/(tabs)/plan');
        }
        return;
      }

      if (intent.kind === 'route') {
        const templateId = normalizeTemplateIdParam(
          intent.params?.templateId as string | string[] | undefined,
        );
        const shouldAutoCreateFirstTrip =
          intent.pathname === '/(tabs)' &&
          Boolean(templateId) &&
          Boolean(firebaseUser?.uid);

        if (shouldAutoCreateFirstTrip && templateId && firebaseUser?.uid) {
          const topicTag = normalizeTemplateTopicParam(
            intent.params?.templateTopic as string | string[] | undefined,
          );
          const refParam = intent.params?.ref;
          const ref = typeof refParam === 'string' ? refParam : undefined;

          const result = await tryAutoCreateFirstTripFromTemplate({
            userId: firebaseUser.uid,
            templateId,
            topicTag,
            ref,
          });

          if (result.ok) {
            useTripSelectionStore.getState().rememberTripId(result.tripId);
            void logAnalyticsEvent('onboarding_first_trip_created', {
              templateId,
              tripId: result.tripId,
              source: 'deeplink',
              topicTag,
              ref,
            });
            router.replace({
              pathname: '/(tabs)/plan',
              params: { tripId: result.tripId },
            });
            return;
          }

          if (result.reason === 'existing_trips') {
            router.replace('/(tabs)/plan');
            return;
          }

          if (result.reason === 'create_failed') {
            console.warn('[NavigationHandler] Auto-create first trip failed; falling back to template browser', {
              templateId,
              message: result.message,
            });
          }
        }
      }

      const params = intent.params;
      if (intent.pathname === pathname) {
        return;
      }
      if (params) {
        router.replace({
          pathname: intent.pathname as any,
          params,
        });
        return;
      }
      router.replace(intent.pathname as any);
    })();
  }, [
    authReady,
    firebaseUser?.email,
    firebaseUser?.uid,
    hasCompletedOnboarding,
    isAuthenticated,
    needsEmailVerification,
    pathname,
    profileName,
    router,
  ]);

  // Handle deep links for Inbox OAuth callbacks
  useEffect(() => {
    const resolveAuthState = (): 'signed_out' | 'signed_in_not_onboarded' | 'signed_in_onboarded' => {
      const state = useUserStore.getState();
      if (!state.isAuthenticated) return 'signed_out';
      return state.hasCompletedOnboarding ? 'signed_in_onboarded' : 'signed_in_not_onboarded';
    };

    const handleDeepLink = async (event: { url: string }) => {
      const parsed = Linking.parse(event.url);
      const host = (parsed.hostname || '').replace(/^\/+/, '');
      const path = (parsed.path || '').replace(/^\/+/, '');
      const pathParts = path.split('/').filter(Boolean);
      const deepLinkParts = host ? [host, ...pathParts] : pathParts;
      const queryParams = parsed.queryParams ?? {};
      processAppleSurfaceQueryParams(queryParams as Record<string, unknown>);

      if (deepLinkParts[0] === 'nav') {
        if (deepLinkParts[1] === 'more-profile') {
          try {
            router.replace('/(tabs)/more?tab=profile' as any);
          } catch {
            router.push('/(tabs)/more?tab=profile' as any);
          }
          return;
        }

        const navTarget = queryStringParam(queryParams.target);
        if (navTarget === 'plan') {
          const tripId = queryStringParam(queryParams.tripId);
          const day = queryStringParam(queryParams.day);
          const planParams: Record<string, string> = {};
          if (tripId) {
            planParams.tripId = tripId;
          }
          if (day) {
            planParams.day = day;
          }
          const appleSurface = queryStringParam(queryParams.appleSurface);
          if (appleSurface) {
            planParams.appleSurface = appleSurface;
          }
          try {
            router.replace({
              pathname: '/(tabs)/plan',
              params: planParams,
            } as never);
          } catch {
            router.push({
              pathname: '/(tabs)/plan',
              params: planParams,
            } as never);
          }
          return;
        }
      }

      // gmail-callback / outlook-callback deep links → app/gmail-callback.tsx & app/outlook-callback.tsx
      // Processing there avoids Expo Router "+not-found" trapping the stack under trip-detection.

      const iq = parsed.queryParams?.invite ?? parsed.queryParams?.pendingInvitationId;
      const normalizedPendingInviteDocId =
        typeof iq === 'string' && iq.trim().length > 0
          ? iq.trim()
          : Array.isArray(iq) && typeof iq[0] === 'string' && iq[0].trim().length > 0
            ? iq[0].trim()
            : undefined;

      if (
        normalizedPendingInviteDocId &&
        (host === 'join-collaboration' ||
          deepLinkParts.includes('join-collaboration') ||
          pathParts.includes('join-collaboration'))
      ) {
        const rawTrip = parsed.queryParams?.trip;
        const rawFrom = parsed.queryParams?.from;
        const invitationTripHint =
          typeof rawTrip === 'string'
            ? rawTrip.trim()
            : Array.isArray(rawTrip) && typeof rawTrip[0] === 'string'
              ? rawTrip[0].trim()
              : '';
        const invitationFromHint =
          typeof rawFrom === 'string'
            ? rawFrom.trim()
            : Array.isArray(rawFrom) && typeof rawFrom[0] === 'string'
              ? rawFrom[0].trim()
              : '';
        const invitationParams: Record<string, string> = {invite: normalizedPendingInviteDocId};
        if (invitationTripHint) {
          invitationParams.trip = invitationTripHint;
        }
        if (invitationFromHint) {
          invitationParams.from = invitationFromHint;
        }
        router.replace({
          pathname: '/join-collaboration',
          params: invitationParams,
        } as never);
        void logAnalyticsEvent('apple_universal_link_opened', {
          route: '/join-collaboration',
          ref: queryStringParam(queryParams.ref),
          utmSource: queryStringParam(queryParams.utm_source),
          authState: resolveAuthState(),
        });
        return;
      }

      const isTemplatesLink =
        host === 'templates' ||
        isTemplatesDeepLinkPath(deepLinkParts) ||
        isTemplatesDeepLinkPath(pathParts);
      if (isTemplatesLink) {
        const topic = normalizeTemplateTopicParam(parsed.queryParams?.topic as string | string[] | undefined);
        const templateId = normalizeTemplateIdParam(
          parsed.queryParams?.templateId as string | string[] | undefined,
        );
        const ref = queryStringParam(queryParams.ref);
        const utmSource = queryStringParam(queryParams.utm_source);
        logTemplateDeepLinkAnalytics({
          topic,
          templateId,
          ref,
          utmSource,
          authState: resolveAuthState(),
        });
        const params: Record<string, string> = {};
        if (topic) {
          params.topic = topic;
        }
        if (templateId) {
          params.templateId = templateId;
        }
        if (ref) {
          params.ref = ref;
        }
        if (utmSource) {
          params.utm_source = utmSource;
        }
        const spotlightType = queryStringParam(queryParams.spotlightType);
        const spotlightEntityId = queryStringParam(queryParams.spotlightEntityId);
        const appIntent = queryStringParam(queryParams.appIntent);
        if (spotlightType) {
          params.spotlightType = spotlightType;
        }
        if (spotlightEntityId) {
          params.spotlightEntityId = spotlightEntityId;
        }
        if (appIntent) {
          params.appIntent = appIntent;
        }
        try {
          router.replace({
            pathname: '/templates',
            params,
          } as never);
        } catch {
          router.push({
            pathname: '/templates',
            params,
          } as never);
        }
        return;
      }

      const invitePathIndex =
        deepLinkParts[0] === 'invite'
          ? 0
          : deepLinkParts[1] === 'invite'
            ? 1
            : -1;
      const isInviteLink = invitePathIndex >= 0 && deepLinkParts.length >= invitePathIndex + 2;
      if (isInviteLink) {
        const linkId = deepLinkParts[invitePathIndex + 1];
        try {
          router.replace(`/invite/${linkId}` as any);
        } catch {
          router.push(`/invite/${linkId}` as any);
        }
        void logAnalyticsEvent('apple_universal_link_opened', {
          route: '/invite/:linkId',
          ref: queryStringParam(queryParams.ref),
          utmSource: queryStringParam(queryParams.utm_source),
          authState: resolveAuthState(),
        });
        return;
      }
    };

    // Listen for deep links while app is running
    const subscription = Linking.addEventListener('url', handleDeepLink);
    
    // Check if app was opened via deep link
    Linking.getInitialURL()
      .then((url) => {
        if (url) {
          handleDeepLink({ url });
        }
      })
      .catch((error) => {
        logger.warn(
          '[RootLayout] Failed to read initial deep-link URL',
          undefined,
          error instanceof Error ? error : new Error(String(error)),
        );
      });

    return () => {
      subscription.remove();
    };
  }, [router]);

  // Expo Router often handles tripvio:// and https:// links without firing Linking listeners.
  useEffect(() => {
    processAppleSurfaceQueryParams(routeParams as Record<string, unknown>);
  }, [
    routeParams.appIntent,
    routeParams.spotlightEntityId,
    routeParams.spotlightType,
  ]);

  return null;
}

function ThemedAppFrame({ children }: { children: React.ReactNode }) {
  const theme = useTheme();
  // Ensures overscroll/bounce (especially above tab bar) never reveals default white.
  return <View style={{ flex: 1, backgroundColor: theme.colors.background }}>{children}</View>;
}

function ThemedStatusBar() {
  const theme = useTheme();
  // In dark mode we want light-content status bar icons; in light mode, dark-content.
  return <StatusBar style={theme.colorScheme === 'dark' ? 'light' : 'dark'} />;
}

function AppleTripSurfaceSyncTracker() {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fallback = setTimeout(() => {
      if (!cancelled) setEnabled(true);
    }, 2500);
    const task = InteractionManager.runAfterInteractions(() => {
      if (!cancelled) setEnabled(true);
    });
    return () => {
      cancelled = true;
      clearTimeout(fallback);
      task.cancel();
    };
  }, []);

  if (!enabled) {
    return null;
  }

  return <AppleTripSurfaceSyncTrackerInner />;
}

function AppleTripSurfaceSyncTrackerInner() {
  useAppleTripSurfaceSync();

  useEffect(() => {
    if (Platform.OS !== 'ios') return;
    if (!applePlatformFlags.phase5Enabled || !applePlatformFlags.widgetNextTripEnabled) return;
    void registerAppleTripSurfaceBackgroundTask();
  }, []);

  return null;
}

function RetentionSessionTracker() {
  const pathname = usePathname();
  const firebaseUser = useUserStore((state) => state.firebaseUser);
  const hasCompletedOnboarding = useUserStore((state) => state.hasCompletedOnboarding);
  const coldStartedForUserRef = useRef<string | null>(null);

  useEffect(() => {
    if (!firebaseUser?.uid) {
      coldStartedForUserRef.current = null;
      return;
    }
    if (coldStartedForUserRef.current === firebaseUser.uid) return;
    coldStartedForUserRef.current = firebaseUser.uid;
    void trackRetentionSession({
      userId: firebaseUser.uid,
      source: 'cold_start',
      route: pathname,
      hasCompletedOnboarding,
    });
  }, [firebaseUser?.uid, hasCompletedOnboarding, pathname]);

  useEffect(() => {
    if (!firebaseUser?.uid) return;

    const subscription = AppState.addEventListener('change', (nextState: AppStateStatus) => {
      if (nextState !== 'active') return;
      void trackRetentionSession({
        userId: firebaseUser.uid,
        source: 'foreground',
        route: pathname,
        hasCompletedOnboarding,
      });
    });

    return () => {
      subscription.remove();
    };
  }, [firebaseUser?.uid, hasCompletedOnboarding, pathname]);

  return null;
}

function RootLayoutInner() {
  const [enableNonCriticalInit, setEnableNonCriticalInit] = useState(false);
  const [enableBottomSheetProvider, setEnableBottomSheetProvider] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fallback = setTimeout(() => {
      if (!cancelled) {
        setEnableNonCriticalInit(true);
        setEnableBottomSheetProvider(true);
      }
    }, 1200);
    const task = InteractionManager.runAfterInteractions(() => {
      if (!cancelled) {
        setEnableNonCriticalInit(true);
        setEnableBottomSheetProvider(true);
      }
    });
    return () => {
      cancelled = true;
      clearTimeout(fallback);
      task.cancel();
    };
  }, []);

  useEffect(() => {
    safeCompleteOAuthWebSession('root_layout_inner_mount');
    logStartupStep('root_layout_inner_mount');
    markStartupMetric('first_root_render');
    if (__DEV__) {
      console.warn(
        '[Metro] TripFlow JS reached RootLayout — if this line is missing in the Metro terminal, the device is not using this bundler (fix host: use --tunnel/--lan or Dev Menu → bundler URL).',
      );
    }
  }, []);

  // Initialize error tracking on mount so startup crashes are captured.
  useEffect(() => {
    logStartupStep('init_error_tracking_start');
    initErrorTracking()
      .then(() => {
        logStartupStep('init_error_tracking_done');
      })
      .catch((error) => {
        logStartupStep('init_error_tracking_failed', {
          message: error instanceof Error ? error.message : String(error),
        });
        logger.warn(
          'Failed to initialize error tracking',
          undefined,
          error instanceof Error ? error : new Error(String(error)),
        );
      })
      .finally(() => {
        logStartupStep('init_error_tracking_finally');
        logStagingSentryDiagnosticsToConsole();
      });

    // Install a global unhandled-rejection hook so any async code path that
    // misses a try/catch or .catch still surfaces to Sentry + logger rather
    // than silently dying. `promise/setimmediate/rejection-tracking` is the
    // tracker that React Native already ships via the `promise` polyfill.
    try {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const rejectionTracking = require('promise/setimmediate/rejection-tracking');
      rejectionTracking.enable({
        allRejections: true,
        onUnhandled: (id: number, rejection: unknown) => {
          const err = rejection instanceof Error ? rejection : new Error(String(rejection));
          logger.warn('Unhandled promise rejection', { id }, err);
          captureException(err, { kind: 'unhandled_rejection', id });
        },
        onHandled: () => {
          // No-op: we don't need to retract previously reported rejections.
        },
      });
      logStartupStep('unhandled_rejection_hook_installed');
    } catch (error) {
      logStartupStep('unhandled_rejection_hook_failed', {
        message: error instanceof Error ? error.message : String(error),
      });
    }
  }, []);

  // Delay non-critical SDK initialization until initial interactions complete.
  useEffect(() => {
    if (!enableNonCriticalInit) {
      return;
    }
    logStartupStep('init_revenuecat_start');
    initRevenueCat()
      .then(() => {
        logStartupStep('init_revenuecat_done');
      })
      .catch((error) => {
        logStartupStep('init_revenuecat_failed', {
          message: error instanceof Error ? error.message : String(error),
        });
        logger.warn(
          'Failed to initialize RevenueCat',
          undefined,
          error instanceof Error ? error : new Error(String(error)),
        );
      });
  }, [enableNonCriticalInit]);

  // Validate Mapbox token configuration on app start (non-blocking)
  useEffect(() => {
    if (__DEV__) {
      const tokenConfigured = isMapboxTokenConfigured();
      if (!tokenConfigured) {
        logger.warn(
          'Mapbox access token not configured. Maps functionality will be limited. See MAPBOX_SETUP.md for setup instructions.',
          {
            hasEnvVar: !!process.env.EXPO_PUBLIC_MAPBOX_ACCESS_TOKEN,
            hasAppJson: !!Constants.expoConfig?.extra?.mapboxAccessToken,
          }
        );
      } else {
        logger.debug('Mapbox access token configured successfully');
      }
    }
  }, []);

  const bottomSheetModule = enableBottomSheetProvider ? loadBottomSheetModule() : null;

  const BottomSheetProvider =
    bottomSheetModule?.BottomSheetModalProvider ?? (({ children }: ProviderProps) => <>{children}</>);

  useEffect(() => {
    logStartupStep('root_layout_providers_resolved', {
      hasGestureModule: true,
      hasSafeAreaModule: true,
      hasBottomSheetModule: Boolean(bottomSheetModule),
      expoGo: isExpoGo(),
    });
  }, [bottomSheetModule]);

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <SafeAreaProvider>
        <BottomSheetProvider>
          <ThemeProvider>
            <ThemedAppFrame>
              <PortalProvider>
                <ToastProvider>
                  <TripErrorBoundary name="RootApp">
                    <AuthInitializer />
                    <RetentionSessionTracker />
                    <AppleTripSurfaceSyncTracker />
                    <NavigationHandler />
                    <NotificationHandler />
                    <ThemedStatusBar />
                    <Stack screenOptions={{ headerShown: false }}>
                      <Stack.Screen name="gmail-callback" options={{ headerShown: false }} />
                      <Stack.Screen name="outlook-callback" options={{ headerShown: false }} />
                      <Stack.Screen name="nav" options={{ headerShown: false }} />
                      <Stack.Screen name="invite/[linkId]" options={{ headerShown: false }} />
                      <Stack.Screen
                        name="trip-detection"
                        options={{
                          presentation: 'fullScreenModal',
                          headerShown: false,
                        }}
                      />
                    </Stack>
                    <CookieConsentBanner />
                    <PremiumGateModal />
                    <PremiumUpsellModal />
                    <PremiumUpsellAutoTrigger />
                  </TripErrorBoundary>
                </ToastProvider>
                {/* Global overlay host (used for minimized sync/upload banners) */}
                <PortalHost name="floating-overlay" />
                {/* iOS SelectField picker sheets — portaled to avoid nested Modal presentation stalls */}
                <PortalHost name="select-picker" />
              </PortalProvider>
            </ThemedAppFrame>
          </ThemeProvider>
        </BottomSheetProvider>
      </SafeAreaProvider>
    </GestureHandlerRootView>
  );
}

function StartupFallbackSplash() {
  const pulse = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, {
          toValue: 1,
          duration: 900,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(pulse, {
          toValue: 0,
          duration: 900,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [pulse]);

  const logoScale = pulse.interpolate({
    inputRange: [0, 1],
    outputRange: [0.97, 1.03],
  });
  const logoOpacity = pulse.interpolate({
    inputRange: [0, 1],
    outputRange: [0.9, 1],
  });

  const useAndroidLogoSplash = Platform.OS === 'android';

  return (
    <View
      style={{
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#0f2210',
      }}
    >
      {useAndroidLogoSplash ? (
        <Animated.Image
          source={startupAppIconImage}
          resizeMode="contain"
          style={{
            width: 220,
            height: 220,
            opacity: logoOpacity,
            transform: [{ scale: logoScale }],
          }}
        />
      ) : (
        <ImageBackground
          source={startupSplashImage}
          resizeMode="cover"
          style={{
            flex: 1,
            width: '100%',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Animated.Image
            source={startupLogoImage}
            resizeMode="contain"
            style={{
              width: 170,
              height: 170,
              opacity: logoOpacity,
              transform: [{ scale: logoScale }],
            }}
          />
        </ImageBackground>
      )}
    </View>
  );
}

export default function RootLayout() {
  // Ensure vector icon fonts are registered before first render.
  // On web, don't block forever: if font fails (CORB, wrong URL) or takes too long, render anyway.
  // On native, `useFonts` should complete quickly; if it never does, unstick the app after a cap.
  const [fontsLoaded, fontError] = useFonts({
    ...Ionicons.font,
  });

  const [webFontTimeout, setWebFontTimeout] = useState(false);
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const t = setTimeout(() => setWebFontTimeout(true), 1500);
    return () => clearTimeout(t);
  }, []);

  const [nativeFontTimeout, setNativeFontTimeout] = useState(false);
  useEffect(() => {
    if (Platform.OS === 'web') return;
    const t = setTimeout(() => setNativeFontTimeout(true), 4000);
    return () => clearTimeout(t);
  }, []);

  const ready =
    fontsLoaded ||
    nativeFontTimeout ||
    (Platform.OS === 'web' && (!!fontError || webFontTimeout));

  useEffect(() => {
    logStartupStep('root_layout_mount');
    return () => {
      logStartupStep('root_layout_unmount');
    };
  }, []);

  useEffect(() => {
    logStartupStep('root_layout_font_state', {
      fontsLoaded,
      hasFontError: Boolean(fontError),
      webFontTimeout,
      nativeFontTimeout,
      ready,
    });
  }, [fontError, fontsLoaded, nativeFontTimeout, ready, webFontTimeout]);

  useEffect(() => {
    if (!ready) return;
    logStartupStep('splash_hide_async_start');
    void SplashScreen.hideAsync()
      .then(() => {
        logStartupStep('splash_hide_async_done');
      })
      .catch((error) => {
        logStartupStep('splash_hide_async_failed', {
          message: error instanceof Error ? error.message : String(error),
        });
      });
  }, [ready]);

  if (!ready) {
    return <StartupFallbackSplash />;
  }

  return <RootLayoutInner />;
}

